import VueCookie from 'vue-cookie'
// configs
import configs from '@/config'
import util from '@/util'

const initOptions = {
  expires: 365,
  sameSite: 'none',
  secure: true,
}

const otherRegions = ['eu']

// Written un-prefixed on every region — the API's session cookies, and ahaFirstPage which the presenter
// reads under one name. A prefixed read looks for a key nothing writes.
const UNPREFIXED_KEYS = new Set(['ahaToken', 'ahaLoggedIn', 'ahaUserId', 'ahaFirstPage'])

const transformKey = (key) => {
  if (!key || UNPREFIXED_KEYS.has(key)) return key

  let prefix = ''

  if (otherRegions.includes(configs.region)) {
    prefix += `${configs.region}-`
  }

  if (prefix) {
    return prefix + key
  }

  return key
}

const _getRootDomain = (domain) => {
  if (!domain) return null

  // configs.domain carries the port locally, and a Domain attribute may not.
  if (domain.includes('localhost')) return 'localhost'

  if (!util.checkWhiteLabelDomain() || configs.env !== 'local') {
    const parts = domain.split('.')

    return parts.slice(-2).join('.')
  }

  return domain
}

const setValueByDomain = (key, value, options = initOptions) => {
  if (!window.location.hostname.includes(configs.domain)) return

  if (configs.domain === 'ahaslides.eu') return

  const newOptions = {
    ...options,
    domain: configs.domain,
  }

  const newKey = transformKey(key)
  VueCookie.set(newKey, value || '', newOptions)
}

const setValue = (key, value, options = initOptions) => {
  const newKey = transformKey(key)

  VueCookie.set(newKey, value || '', options)
}

const getValue = (key) => {
  const newKey = transformKey(key)
  const value = VueCookie.get(newKey)

  if (value === 'true') {
    return true
  }

  if (value === 'false') {
    return false
  }

  return value
}

const deleteValue = (key) => {
  const newKey = transformKey(key)
  VueCookie.delete(newKey)

  if (window.location.hostname.includes(configs.domain)) {
    VueCookie.delete(newKey, {
      domain: configs.domain,
    })
  }
}

const setTokenValue = (key, value, options = initOptions) => {
  if (util.isEuOrWhiteLabel()) {
    setValue(key, value, options)

    return
  }

  const newKey = transformKey(key)

  VueCookie.set(newKey, value, {
    ...options,
    domain: _getRootDomain(configs.domain),
    path: '/',
  })
}

const deleteTokenValue = (key = 'ahaToken') => {
  const newKey = transformKey(key)
  deleteValue(key)

  if (util.isEuOrWhiteLabel()) return

  VueCookie.delete(newKey, {
    domain: _getRootDomain(configs.domain),
    path: '/',
  })
}

const setRootDomainCookie = (key, value, options = {}) => {
  const domain = _getRootDomain(configs.domain)
  // SameSite=None needs Secure, which the local dev server has no TLS for.
  const insecureLocal = domain === 'localhost' ? { sameSite: 'lax', secure: false } : {}

  VueCookie.set(transformKey(key), value, {
    ...initOptions,
    ...insecureLocal,
    ...options,
    domain,
    path: '/',
  })
}

const deleteRootDomainValue = (key) => {
  const newKey = transformKey(key)
  VueCookie.delete(newKey, {
    domain: _getRootDomain(configs.domain),
  })
}

export default {
  deleteValue,
  setValue,
  setValueByDomain,
  getValue,
  setTokenValue,
  deleteTokenValue,
  setRootDomainCookie,
  deleteRootDomainValue,
}
